import { useMemo, useRef, useState } from "react";
import type { VoiceMedia } from "../../voice/useLiveVoice";

// ---------------------------------------------------------------------------
// RecordingCard — the call recording the assistant was asked to play.
//
// The native <audio controls> bar is replaced by a frosted player: the
// waveform IS the scrubber. The element itself is unchanged — same ref, same
// play / pause / ended handlers — so the spoken "stop" / "continue" fast path
// and the speech gate in useLiveVoice keep working exactly as before.
// ---------------------------------------------------------------------------

interface Props {
  media: VoiceMedia;
  language: "ml" | "en";
  registerMediaElement: (el: HTMLAudioElement | null) => void;
  onMediaPlay: () => void;
  onMediaPause: () => void;
  onMediaEnded: () => void;
  onDismiss: () => void;
}

const BARS = 56;

/** Stable pseudo-waveform for a given recording (no decoding needed). */
function barHeights(seed: number): number[] {
  let s = (seed || 1) * 9301 + 49297;
  const rnd = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({ length: BARS }, (_, i) => {
    const envelope = Math.sin((i / BARS) * Math.PI) * 0.34 + 0.66;
    return Math.max(0.28, Math.min(1, (0.45 + rnd() * 0.55) * envelope));
  });
}

const clock = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

export default function RecordingCard({
  media,
  language,
  registerMediaElement,
  onMediaPlay,
  onMediaPause,
  onMediaEnded,
  onDismiss,
}: Props) {
  const elRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(!media.pausedBySpeech && !media.ended);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(media.durationSec ?? 0);
  const bars = useMemo(() => barHeights(media.callId), [media.callId]);

  const ml = language === "ml";
  const progress = duration > 0 ? Math.min(1, time / duration) : 0;

  const attach = (el: HTMLAudioElement | null) => {
    elRef.current = el;
    registerMediaElement(el);
  };

  const toggle = () => {
    const el = elRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = elRef.current;
    if (!el || !duration) return;
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - box.left) / box.width));
    el.currentTime = ratio * duration;
    setTime(el.currentTime);
  };

  const hint = media.pausedBySpeech
    ? ml ? "പോസ് ചെയ്തു — “തുടരൂ” അല്ലെങ്കിൽ “നിർത്ത്” എന്നു പറയൂ." : "Paused — say “continue” or “stop”."
    : media.ended
      ? ml ? "കഴിഞ്ഞു — വീണ്ടും കേൾക്കാൻ പ്ലേ അമർത്തൂ." : "Finished — press play to hear it again."
      : ml ? "സംസാരിച്ചാൽ പോസ് ആകും. “നിർത്ത്” എന്നു പറഞ്ഞാൽ നിർത്തും." : "It pauses the moment you speak. Say “stop” to close it.";

  const subtitle = [media.leadName, media.callerName].filter(Boolean).join(" · ");

  return (
    <div className="xv-rec">
      <div className="xv-rec-top">
        <button className="xv-play" onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
          {playing ? (
            <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <rect x="6" y="4" width="4.5" height="16" rx="1.6" />
              <rect x="13.5" y="4" width="4.5" height="16" rx="1.6" />
            </svg>
          ) : (
            <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M7 4.6c0-1.2 1.3-2 2.4-1.4l9.2 5.4a1.6 1.6 0 0 1 0 2.8l-9.2 5.4A1.6 1.6 0 0 1 7 15.4z" transform="translate(0 1.6)" />
            </svg>
          )}
        </button>

        <div className="xv-rec-meta">
          <div className="xv-rec-title">{media.title}</div>
          <div className="xv-rec-sub">
            {subtitle || (ml ? "കോൾ റെക്കോർഡിംഗ്" : "Call recording")}
          </div>
        </div>

        <div className={`xv-wave${playing && !media.pausedBySpeech ? " is-live" : ""}`} aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <i key={i} style={{ height: `${8 + ((i * 5) % 13)}px` }} />
          ))}
        </div>

        <button className="xv-pill is-icon" onClick={onDismiss} aria-label={ml ? "നിർത്തുക" : "Stop"} title={ml ? "നിർത്തുക" : "Stop"}>
          <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="5" y="5" width="14" height="14" rx="3" />
          </svg>
        </button>
      </div>

      {/* Waveform doubles as the scrubber. */}
      <div
        onClick={seek}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(time)}
        tabIndex={0}
        style={{ display: "flex", alignItems: "center", gap: 2, height: 34, cursor: "pointer" }}
      >
        {bars.map((h, i) => {
          const played = i / BARS <= progress;
          return (
            <span
              key={i}
              style={{
                flex: 1,
                height: `${Math.round(6 + h * 26)}px`,
                borderRadius: 2,
                background: played ? "linear-gradient(180deg, #c1e1d5, #6fb99c)" : "rgba(234,245,241,0.20)",
                boxShadow: played ? "0 0 8px rgba(111,185,156,0.45)" : "none",
                transition: "background 180ms linear, box-shadow 180ms linear",
              }}
            />
          );
        })}
      </div>

      <div className="xv-times">
        <span>{clock(time)}</span>
        <span style={{ color: "rgba(234,245,241,0.55)" }}>{hint}</span>
        <span>{clock(duration)}</span>
      </div>

      <audio
        ref={attach}
        src={media.url}
        autoPlay
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          onMediaPlay();
        }}
        onPause={() => {
          setPlaying(false);
          onMediaPause();
        }}
        onEnded={() => {
          setPlaying(false);
          onMediaEnded();
        }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedData={(e) => setPlaying(!e.currentTarget.paused)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDuration(d);
        }}
        style={{ display: "none" }}
      />
    </div>
  );
}
